// src/pages/manager/production/ManagerCreateProduction.jsx
import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  TextField,
  MenuItem,
  Button,
  Box,
  Table,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Paper,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import api from "../../../utils/api";
import ManagerLayout from "../../../layouts/ManagerLayout";
import SuperAdminLayout from "../../../layouts/SuperAdminLayout";

const CreateProduction = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const superAdminId = user?.id;
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [closingStock, setClosingStock] = useState([]);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    items: [
      {
        product: "",
        unit: "",
        availableQty: 0,
        secondaryQuantity: 0,
        quantity: 0,
        productDetails: null,
      },
    ],
  });

  // load products + recalc & fetch closing-stock
  useEffect(() => {
    async function loadData() {
      // 1) get raw product list
      const pRes = await api.get(`/api/products/get-all/${superAdminId}`);

      // 2) recalculate inwardQuantity on server
      await api.put(`/api/reports/closing-stock/${superAdminId}`);

      // 3) fetch updated closing-stock report
      const stockRes = await api.get(
        `/api/reports/closing-stock/${superAdminId}`
      );
      setClosingStock(stockRes.data);

      // 4) merge into products
      const stockMap = {};
      stockRes.data.forEach((s) => {
        stockMap[s.productName] = s;
      });
      setProducts(
        pRes.data.map((p) => ({
          ...p,
          closingQty: stockMap[p.productName]?.closingQty || 0,
          inwardQty: stockMap[p.productName]?.inwardQty || 0,
        }))
      );
    }
    if (superAdminId) loadData();
  }, [superAdminId]);

  const handleItemChange = (idx, key, value) => {
    const items = [...form.items];
    const it = { ...items[idx] };

    if (key === "product") {
      const pd = products.find((p) => p.productName === value);
      if (pd) {
        it.product = pd.productName;
        it.unit = pd.primaryUnit;
        it.availableQty = pd.closingQty;
        it.productDetails = pd;
        it.quantity = 0;
        it.secondaryQuantity = 0;
      }
    } else if (key === "quantity") {
      it.quantity = value;
      const secFactor = it.productDetails?.secondaryQuantity || 1;
      it.secondaryQuantity = parseFloat((value / secFactor).toFixed(2));
    } else if (key === "secondaryQuantity") {
      it.secondaryQuantity = value;
      const secFactor = it.productDetails?.secondaryQuantity || 1;
      it.quantity = parseFloat((value * secFactor).toFixed(2));
    }
    items[idx] = it;
    setForm({ ...form, items });
  };

  const addRow = () =>
    setForm((f) => ({
      ...f,
      items: [
        ...f.items,
        {
          product: "",
          unit: "",
          availableQty: 0,
          secondaryQuantity: 0,
          quantity: 0,
          productDetails: null,
        },
      ],
    }));

  const removeRow = (idx) =>
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, i) => i !== idx),
    }));

  const handleSubmit = async () => {
    try {
      // 1) ensure inwardStock is up-to-date
      await api.put(`/api/reports/closing-stock/${superAdminId}`);

      // 2) create production record
      await api.post(`/api/production/create`, {
        superAdminId,
        date: form.date,
        items: form.items.map((it) => ({
          product: it.product,
          unit: it.unit,
          primaryQuantity: it.quantity,
          secondaryQuantity: it.secondaryQuantity,
        })),
      });

      alert("Production recorded");
      navigate("/super-admin/production/view");
    } catch (err) {
      console.error(err);
      alert("Failed to record production");
    }
  };

  return (
    <SuperAdminLayout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Daily Production Entry
        </Typography>
        <Box display="flex" gap={2} mb={3}>
          <TextField
            type="date"
            label="Date"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 200 }}
          />
        </Box>
        <Paper sx={{ mb: 3 }}>
          <Table>
            <TableBody>
              {form.items.map((it, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <TextField
                      select
                      label="Product"
                      size="small"
                      sx={{ minWidth: 160 }}
                      value={it.product}
                      onChange={(e) =>
                        handleItemChange(idx, "product", e.target.value)
                      }
                    >
                      {products.map((p) => (
                        <MenuItem key={p._id} value={p.productName}>
                          {p.productName}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      label="Unit"
                      size="small"
                      value={it.unit}
                      InputProps={{ readOnly: true }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      label="Avail Stock"
                      size="small"
                      value={it.availableQty}
                      InputProps={{ readOnly: true }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      label="Primary Qty"
                      type="number"
                      size="small"
                      sx={{ width: 120 }}
                      value={it.quantity}
                      onChange={(e) =>
                        handleItemChange(
                          idx,
                          "quantity",
                          Number(e.target.value)
                        )
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      label="Secondary Qty"
                      type="number"
                      size="small"
                      sx={{ width: 120 }}
                      value={it.secondaryQuantity}
                      onChange={(e) =>
                        handleItemChange(
                          idx,
                          "secondaryQuantity",
                          Number(e.target.value)
                        )
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => removeRow(idx)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
        <Box display="flex" justifyContent="space-between" mb={3}>
          <Button variant="outlined" onClick={addRow}>
            + Add Row
          </Button>
        </Box>
        <Box textAlign="center">
          <Button variant="contained" onClick={handleSubmit}>
            Submit Production
          </Button>
        </Box>
      </Container>
    </SuperAdminLayout>
  );
};

export default CreateProduction;
